import type { ReactNode } from 'react'
import { useMemo } from 'react'

import type { ConfigurationVersionUpdate } from '@/features/configuration/contexts/ConfigurationFileContext'
import { ConfigurationFileContext } from '@/features/configuration/contexts/ConfigurationFileContext'
import {
  useInvalidateResourceJsonQuery,
  useResourceJsonQuery,
} from '@/features/resource/configuration/hooks/useResourceJsonQuery'
import { useUpdateResourceJsonMutation } from '@/features/resource/configuration/hooks/useUpdateResourceJsonMutation'

type ResourceJsonFileProviderProps = {
  children: ReactNode
  resourceId: string
}

export function ResourceJsonFileProvider({ children, resourceId }: Readonly<ResourceJsonFileProviderProps>) {
  const { resourceJsonConfig, isFetched, isLoading } = useResourceJsonQuery(resourceId)
  const { updateResourceJson, isPending } = useUpdateResourceJsonMutation(resourceId)
  const invalidate = useInvalidateResourceJsonQuery()

  const value = useMemo(
    () => ({
      isLatestVersionFetched: isFetched,
      isLatestVersionLoading: isLoading,
      isUpdatePending: isPending,
      latestVersion: resourceJsonConfig?.configurationVersion ?? null,
      updateAndInvalidate: async (update: ConfigurationVersionUpdate) => {
        await updateResourceJson(update)
        await invalidate(resourceId)
      },
    }),
    [isFetched, isLoading, isPending, resourceJsonConfig, updateResourceJson, invalidate, resourceId],
  )

  return <ConfigurationFileContext.Provider value={value}>{children}</ConfigurationFileContext.Provider>
}
